/**
 * 静态奖励 cap 预览 — GET /api/admin/static-reward/preview/:userId
 */
import { useState } from 'react';
import { Input, Typography, Descriptions, message, Card, Statistic, Row, Col } from 'antd';
import { api, ApiError } from '../../api/client';

interface PhpMirrorLevel {
  id?: number;
  title?: string;
  alias?: string;
  price?: number | string;
}

interface PhpMirror {
  my_sum?: string;
  team_sum?: string;
  mp?: string;
  user_level?: PhpMirrorLevel | null;
  mirror_imported_at?: string;
}

interface StaticRewardPreview {
  user_id: number;
  principal_usd: string;
  exit_multiplier: string;
  cap_usd: string;
  cumulative_paid_usd: string;
  remaining_usd: string;
  star_package: string | null;
  reinvest_coin_type: string | null;
  php_mirror?: PhpMirror | null;
}

const STAR_LABEL: Record<string, string> = {
  E1: '一星 (E1)',
  E2: '二星 (E2)',
  E3: '三星 (E3)',
};

const REINVEST_LABEL: Record<string, string> = {
  sp1: '复投 SP1',
  sp2: '复投 SP2',
  sp3: '复投 SP3',
};

function fmtUsd(v: string | number | null | undefined): string {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return `${n.toLocaleString('zh-CN', { maximumFractionDigits: 4 })} USD`;
}

function fmtTime(iso: string | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('zh-CN', { hour12: false });
  } catch {
    return iso;
  }
}

function displayStar(pkg: string | null): string {
  if (!pkg) return '未设置（尚未开通主号/子号快照）';
  return STAR_LABEL[pkg] ?? pkg;
}

function displayReinvest(t: string | null): string {
  if (!t) return '—（主号或无复投币）';
  return REINVEST_LABEL[t] ?? t;
}

export function StaticRewardPreviewPage() {
  const [userId, setUserId] = useState('');
  const [data, setData] = useState<StaticRewardPreview | null>(null);
  const [loading, setLoading] = useState(false);

  async function load() {
    const raw = userId.trim();
    if (!raw) {
      message.warning('请输入玩家编号、用户 ID 或登录名');
      return;
    }
    setLoading(true);
    try {
      const res = await api.get<StaticRewardPreview>(
        `/api/admin/static-reward/preview/${encodeURIComponent(raw)}`,
      );
      setData(res);
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '加载失败');
      setData(null);
    } finally {
      setLoading(false);
    }
  }

  const cap = data ? Number(data.cap_usd) : 0;
  const paid = data ? Number(data.cumulative_paid_usd) : 0;
  const pct = cap > 0 ? Math.min(100, (paid / cap) * 100) : 0;

  return (
    <div>
      <Typography.Title level={4}>静态奖励预览</Typography.Title>
      <Typography.Paragraph type="secondary">
        按「本金 × 出局倍数」计算静态奖终身封顶；已发放金额仅统计流水中的静态腿{' '}
        <code>EP1 + SP1</code>（Node <code>uo_balance_log</code> 或 PHP{' '}
        <code>EP1UOTS/SP1UOTBB</code>），不含当前余额或 <code>uo_</code> 字段。
      </Typography.Paragraph>

      <Input.Search
        style={{ maxWidth: 360, marginBottom: 24 }}
        placeholder="玩家编号 / 用户 ID / 登录名"
        value={userId}
        onChange={(e) => setUserId(e.target.value)}
        onSearch={load}
        enterButton="查询"
        loading={loading}
      />

      {data && (
        <>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={8}>
              <Card size="small">
                <Statistic title="出局封顶 (USD)" value={fmtUsd(data.cap_usd)} />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small">
                <Statistic title="已累计发放 (USD)" value={fmtUsd(data.cumulative_paid_usd)} />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small">
                <Statistic
                  title="剩余可发 (USD)"
                  value={fmtUsd(data.remaining_usd)}
                  valueStyle={{ color: Number(data.remaining_usd) > 0 ? '#3f8600' : '#cf1322' }}
                />
              </Card>
            </Col>
          </Row>

          <Card title="出局规则（Node 计算）" size="small" style={{ marginBottom: 16 }}>
            <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
              <Descriptions.Item label="用户 ID">{data.user_id}</Descriptions.Item>
              <Descriptions.Item label="星级套餐">{displayStar(data.star_package)}</Descriptions.Item>
              <Descriptions.Item label="本金快照 (USD)">{fmtUsd(data.principal_usd)}</Descriptions.Item>
              <Descriptions.Item label="出局倍数">× {data.exit_multiplier}</Descriptions.Item>
              <Descriptions.Item label="封顶金额 (USD)">{fmtUsd(data.cap_usd)}</Descriptions.Item>
              <Descriptions.Item label="已用额度占比">
                {pct.toFixed(1)}%（已发 {fmtUsd(data.cumulative_paid_usd)} / 封顶{' '}
                {fmtUsd(data.cap_usd)}）
              </Descriptions.Item>
              <Descriptions.Item label="复投币种">{displayReinvest(data.reinvest_coin_type)}</Descriptions.Item>
              <Descriptions.Item label="剩余可发 (USD)">
                <Typography.Text strong>{fmtUsd(data.remaining_usd)}</Typography.Text>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {data.php_mirror ? (
            <Card title="PHP 镜像对照（只读）" size="small" type="inner">
              <Typography.Paragraph type="secondary" style={{ marginBottom: 12, fontSize: 12 }}>
                来自旧后台 <code>php_uo_config</code> 镜像，仅供对账；出局计算以 Node 用户表快照为准。
              </Typography.Paragraph>
              <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
                <Descriptions.Item label="个人业绩 (my_sum)">
                  {data.php_mirror.my_sum ?? '—'}
                </Descriptions.Item>
                <Descriptions.Item label="团队业绩 (team_sum)">
                  {data.php_mirror.team_sum ?? '—'}
                </Descriptions.Item>
                <Descriptions.Item label="MP">{data.php_mirror.mp ?? '—'}</Descriptions.Item>
                <Descriptions.Item label="镜像导入时间">
                  {fmtTime(data.php_mirror.mirror_imported_at)}
                </Descriptions.Item>
                <Descriptions.Item label="镜像等级名称" span={2}>
                  {data.php_mirror.user_level?.title ?? '—'}
                </Descriptions.Item>
                <Descriptions.Item label="镜像等级别名" span={2}>
                  {data.php_mirror.user_level?.alias ?? '—'}
                </Descriptions.Item>
                <Descriptions.Item label="镜像等级标价">
                  {data.php_mirror.user_level?.price != null
                    ? fmtUsd(String(data.php_mirror.user_level.price))
                    : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="镜像等级 ID">
                  {data.php_mirror.user_level?.id ?? '—'}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          ) : (
            <Typography.Text type="secondary">该用户在 PHP 镜像表中无记录。</Typography.Text>
          )}
        </>
      )}

      {!data && !loading && (
        <Typography.Text type="secondary">
          输入玩家编号、用户 ID 或登录名后点击「查询」，查看出局倍数与剩余静态额度。
        </Typography.Text>
      )}
    </div>
  );
}
